/**
 * Browser-side image utilities — file upload (resize + JPEG-encode) and
 * naive white-background removal for logos.
 */

/** Reads a File from <input type="file"> and returns a downscaled data URL.
 *  PNG files are kept as PNG to preserve transparency; everything else becomes JPEG. */
export async function fileToDataUrl(
  file: File,
  options: { maxSize?: number; quality?: number } = {}
): Promise<string> {
  const maxSize = options.maxSize ?? 1600
  const quality = options.quality ?? 0.86
  const keepAlpha = file.type === 'image/png' || file.name.toLowerCase().endsWith('.png')

  const bitmap = await createImageBitmap(file)
  const ratio = Math.min(1, maxSize / Math.max(bitmap.width, bitmap.height))
  const w = Math.round(bitmap.width * ratio)
  const h = Math.round(bitmap.height * ratio)

  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas not available.')
  ctx.drawImage(bitmap, 0, 0, w, h)
  return keepAlpha ? canvas.toDataURL('image/png') : canvas.toDataURL('image/jpeg', quality)
}

/**
 * Naive background removal: pixels brighter than `threshold` in all RGB
 * channels become transparent. Returns a PNG data URL with alpha.
 */
export async function removeWhiteBackground(
  imageUrl: string,
  options: { threshold?: number; feather?: number } = {}
): Promise<string> {
  const threshold = options.threshold ?? 235
  const feather = options.feather ?? 18

  const img = await loadImage(imageUrl)

  const maxSize = 1200
  const naturalW = img.naturalWidth || img.width
  const naturalH = img.naturalHeight || img.height
  const ratio = Math.min(1, maxSize / Math.max(naturalW, naturalH))
  const w = Math.max(1, Math.round(naturalW * ratio))
  const h = Math.max(1, Math.round(naturalH * ratio))

  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (!ctx) throw new Error('Canvas context not available.')
  ctx.drawImage(img, 0, 0, w, h)

  let imageData: ImageData
  try {
    imageData = ctx.getImageData(0, 0, w, h)
  } catch {
    throw new Error(
      'Cannot read image pixels (CORS). Please upload the logo locally and try again.'
    )
  }
  const data = imageData.data

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i]
    const g = data[i + 1]
    const b = data[i + 2]
    const minC = Math.min(r, g, b)
    if (minC >= threshold) {
      data[i + 3] = 0
    } else if (minC >= threshold - feather) {
      const t = (threshold - minC) / feather
      data[i + 3] = Math.round(data[i + 3] * Math.min(1, t))
    }
  }

  ctx.putImageData(imageData, 0, 0)
  return canvas.toDataURL('image/png')
}

/** Converts a data URL or blob URL to a File object for upload */
export async function dataUrlToFile(dataUrl: string, filename: string): Promise<File> {
  const res = await fetch(dataUrl)
  const blob = await res.blob()
  return new File([blob], filename, { type: blob.type })
}

/** Renders an HTML element to a canvas and returns a PNG data URL */
export async function elementToPng(element: HTMLElement): Promise<string> {
  // Wait for all images inside the element to be loaded
  const images = element.querySelectorAll('img')
  await Promise.all(
    Array.from(images).map(
      (img) =>
        new Promise<void>((resolve) => {
          if (img.complete) {
            resolve()
          } else {
            img.onload = () => resolve()
            img.onerror = () => resolve()
          }
        })
    )
  )

  // Load html2canvas lazily so it is code-split out of the main bundle.
  // Some bundlers expose it as the module's default, others as the namespace
  // itself — handle both so the call site is resolution-agnostic.
  const mod = await import('html2canvas')
  const html2canvas = (mod as any).default ?? mod
  const canvas = await html2canvas(element, {
    scale: 1,
    width: element.offsetWidth,
    height: element.offsetHeight,
    useCORS: true,
    allowTaint: true,
    backgroundColor: '#FFFFFF',
    logging: false,
    imageTimeout: 15000,
  })
  return canvas.toDataURL('image/png')
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('Image could not be loaded (network or CORS error).'))
    img.src = src
  })
}
